import { useTranslations } from "next-intl";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ASTROMANIA_BRAND } from "@lms/backend/lib/platform/astromania-brand";
import { showsAstromaniaSiteFooter } from "@/lib/tenant-brand";

type TenantFooterProps = {
  tenantSlug: string;
  /** SPA-11: the school's own name and logo; Astromania's site content only on its tenant. */
  tenantName: string;
  logoUrl?: string | null;
};

export function TenantFooter({ tenantSlug, tenantName, logoUrl = null }: TenantFooterProps) {
  const t = useTranslations("footer");
  const base = `/t/${tenantSlug}`;
  const brand = ASTROMANIA_BRAND;
  const siteFooter = showsAstromaniaSiteFooter(tenantSlug);
  // MT-1 FR-21: the logo is the school's own data; none set means none shown.
  const logoSrc = logoUrl;
  // MT-1 FR-8.7: a logo that fails to load shows the school's name instead (keyed by URL, so a
  // new logo is tried again).
  const [failedLogoSrc, setFailedLogoSrc] = useState<string | null>(null);
  const showLogo = logoSrc !== null && logoSrc !== failedLogoSrc;

  const quickLinks = [
    { href: `${base}/courses`, label: t("courses"), external: false },
    { href: `${base}/dashboard`, label: t("dashboard"), external: false },
    { href: `${base}/login`, label: t("signIn"), external: false },
    // H4: the Terms of use and Privacy notice are public pages.
    { href: `${base}/terms`, label: t("termsOfUse"), external: false },
    { href: `${base}/privacy`, label: t("privacyNotice"), external: false },
    ...(siteFooter
      ? [
          { href: `${brand.siteUrl}/about-us`, label: t("aboutUs"), external: true },
          {
            href: `${brand.siteUrl}/book-an-appointment`,
            label: t("appointments"),
            external: true,
          },
          { href: `${brand.siteUrl}/contact-us`, label: t("contactUs"), external: true },
        ]
      : []),
  ];

  return (
    <footer
      className="mt-auto shrink-0 border-t border-white/10 bg-black text-white"
      data-testid="tenant-footer"
    >
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            {showLogo ? (
              <img
                src={logoSrc}
                alt={tenantName}
                className="h-auto w-44"
                data-testid="footer-logo"
                onError={() => setFailedLogoSrc(logoSrc)}
              />
            ) : (
              <p className="font-serif text-2xl" data-testid="footer-tenant-name">
                {tenantName}
              </p>
            )}
            {siteFooter ? (
              <p className="mt-4 text-sm font-light leading-relaxed text-white/85">{t("about")}</p>
            ) : null}
          </div>

          <div className="md:col-span-2">
            <h2 className="font-serif text-xl text-[var(--accent-ink-dark)]">{t("quickLinks")}</h2>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm font-light">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[var(--accent-ink-dark)]"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="hover:text-[var(--accent-ink-dark)]">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {siteFooter ? (
            <>
              <div className="md:col-span-4">
                <h2 className="font-serif text-xl text-[var(--accent-ink-dark)]">
                  {t("contactInfo")}
                </h2>
                <ul className="mt-3 space-y-3 text-sm font-light text-white/90">
                  <li className="flex items-start gap-3">
                    <PinIcon />
                    <span>{brand.address}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <PhoneIcon />
                    <a
                      href={`tel:${brand.phones[0]!.replace(/\s/g, "")}`}
                      className="hover:text-[var(--accent-ink-dark)]"
                    >
                      {brand.phones.join(", ")}
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MailIcon />
                    <a
                      href={`mailto:${brand.email}`}
                      className="hover:text-[var(--accent-ink-dark)]"
                    >
                      {brand.email}
                    </a>
                  </li>
                </ul>
              </div>

              <div className="md:col-span-2 md:text-end">
                <h2 className="font-serif text-xl text-[var(--accent-ink-dark)]">
                  {t("followUs")}
                </h2>
                <div className="mt-3 flex items-center gap-3 md:justify-end">
                  <SocialLink href={brand.social.facebook} label="Facebook">
                    <FacebookIcon />
                  </SocialLink>
                  <SocialLink href={brand.social.instagram} label="Instagram">
                    <InstagramIcon />
                  </SocialLink>
                  <SocialLink href={brand.social.youtube} label="YouTube">
                    <YouTubeIcon />
                  </SocialLink>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-white/25 pt-5 text-xs text-white/70 sm:flex-row sm:items-center sm:justify-between sm:text-sm">
          <p>{t("copyright", { year: new Date().getFullYear(), name: tenantName })}</p>
          {siteFooter ? (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:justify-end">
              <a
                href={brand.legal.privacy}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                {t("privacyPolicy")}
              </a>
              <span aria-hidden>|</span>
              <a
                href={brand.legal.terms}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                {t("terms")}
              </a>
              <span aria-hidden>|</span>
              <a
                href={brand.legal.refund}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                {t("refundPolicy")}
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </footer>
  );
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--brand-ink)] transition hover:bg-white hover:text-[var(--accent-ink-dark)]"
    >
      {children}
    </a>
  );
}

function iconClass() {
  return "mt-0.5 h-4 w-4 shrink-0 fill-current";
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" className={iconClass()} aria-hidden>
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className={iconClass()} aria-hidden>
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.6 1 1 0 0 1-.25 1z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className={iconClass()} aria-hidden>
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden>
      <path d="M14 9h3V6h-3c-2.2 0-4 1.8-4 4v2H7v3h3v7h3v-7h3l1-3h-4v-2c0-.6.4-1 1-1z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden>
      <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.5A3.5 3.5 0 1 1 8.5 12 3.5 3.5 0 0 1 12 8.5zm0 2A1.5 1.5 0 1 0 13.5 12 1.5 1.5 0 0 0 12 10.5zM17.5 7.2a1 1 0 1 1-1 1 1 1 0 0 1 1-1z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden>
      <path d="M23 12.2s0-3.2-.4-4.7c-.2-.8-.9-1.5-1.7-1.7C19.4 5.4 12 5.4 12 5.4s-7.4 0-8.9.4c-.8.2-1.5.9-1.7 1.7C1 9 1 12.2 1 12.2s0 3.2.4 4.7c.2.8.9 1.5 1.7 1.7 1.5.4 8.9.4 8.9.4s7.4 0 8.9-.4c.8-.2 1.5-.9 1.7-1.7.4-1.5.4-4.7.4-4.7zM9.8 15.5V8.9l6.2 3.3z" />
    </svg>
  );
}
